import { loginRequestSchema } from "@corklog/shared";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { ApiError, logIn } from "../api.js";
import { useSession } from "../session.js";

/**
 * The form validates with the same schema the server validates with, so the
 * two cannot disagree about what a login request is. The messages are the
 * interface's own: they say what is wrong and what to do, and they do not
 * apologise.
 */
export function SignIn() {
  const { t } = useTranslation();
  const { signedIn } = useSession();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    const parsed = loginRequestSchema.safeParse({ email, password });
    if (!parsed.success) {
      const badEmail = parsed.error.issues.some((issue) => issue.path[0] === "email");
      setError(t(badEmail ? "signIn.invalidEmail" : "signIn.missingPassword"));
      return;
    }

    setBusy(true);
    try {
      signedIn(await logIn(parsed.data));
      void navigate("/");
    } catch (caught) {
      const unreachable = caught instanceof ApiError && caught.status === 0;
      setError(t(unreachable ? "signIn.serverDown" : "signIn.wrongCredentials"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="max-w-sm" noValidate>
      <h1 className="font-display text-3xl">{t("signIn.title")}</h1>
      <p className="text-muted mt-2 text-sm">{t("signIn.intro")}</p>

      <label className="mt-8 block">
        <span className="text-muted text-sm">{t("signIn.email")}</span>
        <input
          type="email"
          autoComplete="username"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          className="border-line focus:border-accent mt-1 block w-full border-b bg-transparent py-2 outline-none"
        />
      </label>

      <label className="mt-5 block">
        <span className="text-muted text-sm">{t("signIn.password")}</span>
        <input
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          className="border-line focus:border-accent mt-1 block w-full border-b bg-transparent py-2 outline-none"
        />
      </label>

      {error !== null && (
        <p role="alert" className="text-past mt-5 text-sm">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={busy}
        className="bg-accent text-ground mt-8 px-5 py-2 font-medium disabled:opacity-60"
      >
        {busy ? t("signIn.working") : t("signIn.submit")}
      </button>
    </form>
  );
}
